// external
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

// internal
import COEHeaderComponent from '../../components/coe-header/coe-header.component';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  Paper,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ReplayIcon from '@mui/icons-material/Replay';
import EquipmentModal from '../../components/equipment-modal/equipment-modal';
import AddEquipmentModal from '../../components/equipment-modal/add-equipment-modal';
import RequestAthleteManagementDialog from '../../components/dialogs/request-athlete-management.dialog';
import EquipmentManagersDialog from '../../components/dialogs/equipment-managers.dialog';
import { useSnackbar } from 'notistack';

const EQUIPMENT_TYPE_SKIS_ID = 1;
import useService from '../../hooks/service.hook';
import EquipmentService, {
  EMPTY_EQUIPMENT_SEARCH_SUMMARY,
  type EquipmentSearchSummary,
} from '../../services/equipments.service';
import EquipmentSharingService from '../../services/equipment-sharing.service';
import COEEmptyState from '../../components/coe-empty-state/coe-empty-state.component';
import { getMeasurementUnit } from '../../helpers/general.helpers';
import { UserAvatar } from '../../components/user-avatar/user-avatar.component';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import EquipmentSearchCard, { type OwnerPayload, type SearchEquipmentItem } from './equipment-search-card.component';
import EquipmentResultStatsBar from './equipment-result-stats-bar';
import { combineShoeSizeDisplay, getStandardValue } from '../../helpers/equipment.helpers';
import type { User } from '../../models/user.models';

type ViewMode = 'details' | 'cards';

type SortColumn =
  | 'name'
  | 'public_id'
  | 'is_owner'
  | 'brand'
  | 'year'
  | 'type'
  | 'subtype'
  | 'distance_used'
  | 'default_sport'
  | 'created_at'
  | 'updated_at';

interface OwnerEquipmentGroup {
  owner: OwnerPayload;
  equipments: SearchEquipmentItem[];
  total_count: number;
}

interface ManagedOwnerPayload {
  id: number;
  firstname: string;
  lastname: string;
  profile_image: string | null;
}

interface EquipmentSubtypeOption {
  id: number;
  name: string;
}

interface EquipmentTypeOption {
  id: number;
  name: string;
  subtypes: EquipmentSubtypeOption[];
}

interface PendingInvitation {
  id: number;
  email: string;
  firstname?: string;
  lastname?: string;
  created_at: string;
  status: string;
  invitation_type?: 'owner_to_manager' | 'owner_transfer' | 'manager_to_athlete';
}

function parseNumericFilterFromUrl(raw: string | null): number | null {
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function ownerSortKey(owner: OwnerPayload, groupIndex: number): number {
  return owner.id != null ? owner.id : -1 - groupIndex;
}

function normalizeManagedOwner(raw: Record<string, unknown>): ManagedOwnerPayload | null {
  const idRaw = raw.id;
  let id: number | null = null;
  if (typeof idRaw === 'number' && Number.isFinite(idRaw)) id = idRaw;
  else if (typeof idRaw === 'string') {
    const n = parseInt(idRaw, 10);
    if (!Number.isNaN(n)) id = n;
  }
  if (id == null) return null;
  return {
    id,
    firstname: String(raw.firstname ?? ''),
    lastname: String(raw.lastname ?? ''),
    profile_image: (raw.profile_image as string | null | undefined) ?? null,
  };
}

function isSelfUserId(ownerUserId: number | null | undefined, currentUserId: number | null): boolean {
  return currentUserId != null && ownerUserId != null && ownerUserId === currentUserId;
}

function distanceUsedSortValue(item: SearchEquipmentItem): number | null {
  const raw = item.totalDistanceUsed;
  const n = typeof raw === 'number' ? raw : raw != null ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : null;
}

function formatDistanceUsedCell(item: SearchEquipmentItem, distanceUnit: string): string {
  const rawDist = item.totalDistanceUsed;
  const distNum = typeof rawDist === 'number' ? rawDist : rawDist != null ? Number(rawDist) : NaN;
  if (!Number.isFinite(distNum) || distNum <= 0) return '—';
  return `${distNum} ${distanceUnit}`;
}

function equipmentTypeName(item: SearchEquipmentItem): string {
  return (item.equipment_type?.name ?? '').trim().toLowerCase();
}

function isSkiItem(item: SearchEquipmentItem): boolean {
  return equipmentTypeName(item) === 'xc skis';
}

function isShoeItem(item: SearchEquipmentItem): boolean {
  return equipmentTypeName(item) === 'shoes';
}

function formatDefaultSportCell(item: SearchEquipmentItem): string {
  if (item.default_sport?.name?.trim()) {
    return item.default_sport_type?.name?.trim()
      ? `${item.default_sport.name.trim()} · ${item.default_sport_type.name.trim()}`
      : item.default_sport.name.trim();
  }
  return '—';
}

type EquipmentTableVariant = 'skis' | 'shoes' | 'other';

const EQUIPMENT_TABLE_SX = {
  '& thead .MuiTableCell-root': {
    whiteSpace: 'nowrap',
  },
  '& tbody .MuiTableCell-root': {
    verticalAlign: 'top',
  },
  '& .MuiTableCell-root': {
    px: 0.5,
    py: 0.5,
    fontSize: '0.75rem',
  },
} as const;

function compareValues(
  a: string | number | boolean | null | undefined,
  b: string | number | boolean | null | undefined,
  direction: 'asc' | 'desc'
): number {
  const mult = direction === 'asc' ? 1 : -1;
  if (a == null && b == null) return 0;
  if (a == null) return -1 * mult;
  if (b == null) return 1 * mult;
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    if (a === b) return 0;
    return (a ? 1 : -1) * mult;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    if (a === b) return 0;
    return (a < b ? -1 : 1) * mult;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true }) * mult;
}

function sortEquipmentsForOwner(
  items: SearchEquipmentItem[],
  sort: { column: SortColumn; direction: 'asc' | 'desc' } | undefined
): SearchEquipmentItem[] {
  if (!sort) return items;
  const { column, direction } = sort;
  return [...items].sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case 'name':
        cmp = compareValues(a.name, b.name, direction);
        break;
      case 'public_id':
        cmp = compareValues(a.public_id ?? '', b.public_id ?? '', direction);
        break;
      case 'is_owner':
        cmp = compareValues(!!a.is_owner, !!b.is_owner, direction);
        break;
      case 'brand':
        cmp = compareValues(a.brand ?? '', b.brand ?? '', direction);
        break;
      case 'year':
        cmp = compareValues(a.year != null ? Number(a.year) : null, b.year != null ? Number(b.year) : null, direction);
        break;
      case 'type':
        cmp = compareValues(a.equipment_type?.name ?? '', b.equipment_type?.name ?? '', direction);
        break;
      case 'subtype':
        cmp = compareValues(a.equipment_subtype?.name ?? '', b.equipment_subtype?.name ?? '', direction);
        break;
      case 'distance_used':
        cmp = compareValues(distanceUsedSortValue(a), distanceUsedSortValue(b), direction);
        break;
      case 'default_sport':
        cmp = compareValues(a.default_sport?.name ?? '', b.default_sport?.name ?? '', direction);
        break;
      case 'created_at':
        cmp = compareValues(
          a.created_at ? new Date(a.created_at).getTime() : null,
          b.created_at ? new Date(b.created_at).getTime() : null,
          direction
        );
        break;
      case 'updated_at':
        cmp = compareValues(
          a.updated_at ? new Date(a.updated_at).getTime() : null,
          b.updated_at ? new Date(b.updated_at).getTime() : null,
          direction
        );
        break;
      default:
        cmp = 0;
    }
    if (cmp !== 0) return cmp;
    return direction === 'asc' ? a.id - b.id : b.id - a.id;
  });
}

const EquipmentPage: FC<{ teamId?: number; embedded?: boolean }> = ({ teamId, embedded = false }) => {
  const isTeamMode = teamId != null;
  const { enqueueSnackbar } = useSnackbar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | undefined>();
  const [ownerGroups, setOwnerGroups] = useState<OwnerEquipmentGroup[]>([]);
  const [searchSummary, setSearchSummary] = useState<EquipmentSearchSummary>(EMPTY_EQUIPMENT_SEARCH_SUMMARY);
  const [managedOwners, setManagedOwners] = useState<ManagedOwnerPayload[]>([]);
  const [athletesLoading, setAthletesLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);
  const [pendingInvitesLoading, setPendingInvitesLoading] = useState(!isTeamMode);
  const [pageLoading, setPageLoading] = useState(true);
  const equipmentService = useService(EquipmentService);
  const equipmentSharingService = useService(EquipmentSharingService);
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [searchQueryInput, setSearchQueryInput] = useState(() => searchParams.get('q') || '');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [brandFilter, setBrandFilter] = useState(() => searchParams.get('brand') || '');
  const [brandFilterInput, setBrandFilterInput] = useState(() => searchParams.get('brand') || '');
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentTypeOption[]>([]);
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState<number | null>(() =>
    parseNumericFilterFromUrl(searchParams.get('equipment_type_id'))
  );
  const [equipmentTypeFilterInput, setEquipmentTypeFilterInput] = useState<number | null>(() =>
    parseNumericFilterFromUrl(searchParams.get('equipment_type_id'))
  );
  const [equipmentSubtypeFilter, setEquipmentSubtypeFilter] = useState<number | null>(() =>
    parseNumericFilterFromUrl(searchParams.get('equipment_subtype_id'))
  );
  const [equipmentSubtypeFilterInput, setEquipmentSubtypeFilterInput] = useState<number | null>(() =>
    parseNumericFilterFromUrl(searchParams.get('equipment_subtype_id'))
  );
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [sortByOwnerId, setSortByOwnerId] = useState<Record<number, { column: SortColumn; direction: 'asc' | 'desc' }>>(
    {}
  );
  const [selectedOwnerUserId, setSelectedOwnerUserId] = useState<number | null>(null);
  const [athleteUserIdForModal, setAthleteUserIdForModal] = useState<number | undefined>(undefined);
  const [athleteNameForModal, setAthleteNameForModal] = useState<string | undefined>(undefined);
  const [skiModalInitialTab, setSkiModalInitialTab] = useState(0);
  const hasInitializedSearchRef = useRef(false);
  const [requestAthleteDialogOpen, setRequestAthleteDialogOpen] = useState(false);
  const [managersDialogOpen, setManagersDialogOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const equipmentIdFromUrl = searchParams.get('equipment_id');
  const equipmentTabFromUrl = searchParams.get('equipment_tab');

  const currentUserId = useMemo((): number | null => {
    try {
      const raw = window.localStorage.getItem('user');
      if (!raw) return null;
      const u = JSON.parse(raw) as User;
      return typeof u?.id === 'number' && !Number.isNaN(u.id) ? u.id : null;
    } catch {
      return null;
    }
  }, []);

  const currentUser = useMemo((): User | null => {
    try {
      const raw = window.localStorage.getItem('user');
      if (!raw) return null;
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  }, []);

  const searchOptions = useMemo(
    () => ({
      brand: brandFilter.trim() || undefined,
      equipment_type_id: equipmentTypeFilter ?? undefined,
      equipment_subtype_id: equipmentSubtypeFilter ?? undefined,
    }),
    [brandFilter, equipmentTypeFilter, equipmentSubtypeFilter]
  );

  const selectedTypeForInput = useMemo(
    () => equipmentTypes.find((type) => type.id === equipmentTypeFilterInput) ?? null,
    [equipmentTypeFilterInput, equipmentTypes]
  );
  const subtypeOptionsForInput = useMemo(() => selectedTypeForInput?.subtypes ?? [], [selectedTypeForInput]);

  const selectedGroupContext = useMemo(() => {
    if (selectedOwnerUserId != null) {
      const groupIndex = ownerGroups.findIndex((g) => g.owner.id === selectedOwnerUserId);
      if (groupIndex < 0) return null;
      const group = ownerGroups[groupIndex];
      const ownerKey = ownerSortKey(group.owner, groupIndex);
      const sort = sortByOwnerId[ownerKey];
      const sortedItems = sortEquipmentsForOwner(group.equipments, sort);
      return { group, ownerKey, sortedItems };
    }
    const group = ownerGroups[0];
    if (!group) return null;
    const ownerKey = ownerSortKey(group.owner, 0);
    const sort = sortByOwnerId[ownerKey];
    const sortedItems = sortEquipmentsForOwner(group.equipments, sort);
    return { group, ownerKey, sortedItems };
  }, [ownerGroups, selectedOwnerUserId, sortByOwnerId]);

  const selectedOwnerInfo = useMemo((): ManagedOwnerPayload | null => {
    if (selectedOwnerUserId == null) return null;
    const found = managedOwners.find((o) => o.id === selectedOwnerUserId);
    if (found) return found;
    // Fall back to current user when they are selected (not in managedOwners).
    if (selectedOwnerUserId === currentUserId && currentUser) {
      return {
        id: currentUserId,
        firstname: currentUser.firstName,
        lastname: currentUser.lastName,
        profile_image: currentUser.profileImage ?? null,
      };
    }
    return null;
  }, [selectedOwnerUserId, managedOwners, currentUserId, currentUser]);

  const filteredSidebarOwners = useMemo(() => {
    const list = managedOwners;
    const filter = sidebarSearchQuery.trim().toLowerCase();
    if (!filter) return list;
    return list.filter((owner) => {
      const name = `${owner.firstname ?? ''} ${owner.lastname ?? ''}`.trim().toLowerCase();
      return name.includes(filter);
    });
  }, [sidebarSearchQuery, managedOwners]);

  const syncQueryParams = () => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('q', searchQuery);
    if (brandFilter.trim()) newParams.set('brand', brandFilter.trim());
    else newParams.delete('brand');
    if (equipmentTypeFilter != null) newParams.set('equipment_type_id', String(equipmentTypeFilter));
    else newParams.delete('equipment_type_id');
    if (equipmentSubtypeFilter != null) newParams.set('equipment_subtype_id', String(equipmentSubtypeFilter));
    else newParams.delete('equipment_subtype_id');
    newParams.delete('owner');
    newParams.delete('is_public');
    newParams.delete('ownership');
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const fetchOwnerGroups = () => {
    const searchPromise =
      teamId != null
        ? equipmentService.searchTeamEquipment(teamId, searchQuery, searchOptions)
        : equipmentService.searchEquipment(searchQuery, searchOptions);
    return searchPromise.then((res) => {
      const data = res.data?.data;
      setOwnerGroups(data?.results ?? []);
      setSearchSummary((data?.summary as EquipmentSearchSummary | undefined) ?? EMPTY_EQUIPMENT_SEARCH_SUMMARY);
    });
  };

  useEffect(() => {
    let cancelled = false;

    equipmentService
      .getEquipmentTypes()
      .then((res) => {
        if (cancelled) return;
        const payload = Array.isArray(res.data?.data) ? res.data.data : [];
        const normalized = payload
          .map((raw): EquipmentTypeOption | null => {
            const id = Number(raw?.id);
            if (!Number.isFinite(id)) return null;
            const subtypesRaw = Array.isArray(raw?.subtypes) ? raw.subtypes : [];
            const subtypes = subtypesRaw
              .map((subtype): EquipmentSubtypeOption | null => {
                const subtypeId = Number(subtype?.id);
                if (!Number.isFinite(subtypeId)) return null;
                return { id: subtypeId, name: String(subtype?.name ?? '') };
              })
              .filter((subtype): subtype is EquipmentSubtypeOption => subtype != null);
            return { id, name: String(raw?.name ?? ''), subtypes };
          })
          .filter((type): type is EquipmentTypeOption => type != null);
        setEquipmentTypes(normalized.reverse());
      })
      .catch(() => {
        if (!cancelled) setEquipmentTypes([]);
      });

    setAthletesLoading(true);
    equipmentService
      .getManagedOwners(teamId)
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.data;
        const ownersRaw = Array.isArray(payload) ? payload : Array.isArray(res.data) ? res.data : [];
        const owners = ownersRaw
          .map((o) => normalizeManagedOwner(o as Record<string, unknown>))
          .filter((o): o is ManagedOwnerPayload => o != null);
        setManagedOwners(owners);
      })
      .catch(() => {
        if (!cancelled) setManagedOwners([]);
      })
      .finally(() => {
        if (!cancelled) setAthletesLoading(false);
      });

    if (!isTeamMode) {
      setPendingInvitesLoading(true);
      equipmentSharingService
        .getSentEquipmentInvitations()
        .then((res) => {
          if (cancelled) return;
          const all: PendingInvitation[] = Array.isArray(res.data?.data) ? res.data.data : [];
          setPendingInvites(all.filter((i) => i.invitation_type === 'manager_to_athlete'));
        })
        .catch(() => {
          if (!cancelled) setPendingInvites([]);
        })
        .finally(() => {
          if (!cancelled) setPendingInvitesLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [equipmentService, equipmentSharingService, teamId, isTeamMode]);

  useEffect(() => {
    // Clear the input subtype when the input type no longer contains it.
    const inputSubtypes = selectedTypeForInput?.subtypes ?? [];
    if (!inputSubtypes.some((s) => s.id === equipmentSubtypeFilterInput)) {
      setEquipmentSubtypeFilterInput(null);
    }

    // Clear the applied subtype when the applied type no longer contains it.
    // Guard against running before types are loaded to avoid clobbering URL-initialised state.
    if (equipmentTypes.length > 0) {
      const appliedType = equipmentTypes.find((t) => t.id === equipmentTypeFilter) ?? null;
      const appliedSubtypes = appliedType?.subtypes ?? [];
      if (equipmentSubtypeFilter != null && !appliedSubtypes.some((s) => s.id === equipmentSubtypeFilter)) {
        setEquipmentSubtypeFilter(null);
      }
    }
  }, [selectedTypeForInput, equipmentSubtypeFilterInput, equipmentTypes, equipmentTypeFilter, equipmentSubtypeFilter]);

  useEffect(() => {
    const isInitialSearch = !hasInitializedSearchRef.current;
    if (isInitialSearch) {
      setPageLoading(true);
    } else {
      setSearchLoading(true);
    }
    setSearchError('');
    syncQueryParams();
    fetchOwnerGroups()
      .catch(() => {
        setSearchError('Failed to search equipment.');
      })
      .finally(() => {
        hasInitializedSearchRef.current = true;
        setPageLoading(false);
        setSearchLoading(false);
      });
  }, [searchQuery, searchOptions, equipmentService, teamId]);

  useEffect(() => {
    // Build valid IDs: current user first, then managed athletes, then any remaining from search groups.
    const validIds: number[] = [];
    const seen = new Set<number>();
    if (currentUserId != null) {
      seen.add(currentUserId);
      validIds.push(currentUserId);
    }
    for (const o of filteredSidebarOwners) {
      if (!seen.has(o.id)) {
        seen.add(o.id);
        validIds.push(o.id);
      }
    }
    for (const g of ownerGroups) {
      const id = g.owner.id;
      if (id != null && !seen.has(id)) {
        seen.add(id);
        validIds.push(id);
      }
    }
    if (validIds.length === 0) {
      setSelectedOwnerUserId(null);
      return;
    }
    setSelectedOwnerUserId((prev) => {
      if (prev != null && validIds.includes(prev)) return prev;
      return validIds[0] ?? null;
    });
  }, [filteredSidebarOwners, ownerGroups, currentUserId]);

  useEffect(() => {
    if (!equipmentIdFromUrl) return;
    const id = parseInt(equipmentIdFromUrl, 10);
    if (Number.isNaN(id)) return;

    let cancelled = false;
    equipmentService
      .getEquipment(id)
      .then((res) => {
        if (cancelled) return;
        const equipment = res.data?.data;
        if (!equipment) return;
        const typeId = equipment.equipment_type?.id ?? equipment.equipment_type_id;
        const wantRaceLog = equipmentTabFromUrl === 'race-log';
        const wantManagersTab = equipmentTabFromUrl === 'managers';
        setSelectedEquipmentId(id);
        setSkiModalInitialTab(
          wantManagersTab && equipment.is_owner ? 3 : wantRaceLog && typeId === EQUIPMENT_TYPE_SKIS_ID ? 2 : 0
        );
        setSidebarOpen(true);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [equipmentIdFromUrl, equipmentTabFromUrl, equipmentService]);

  const runSearchRefresh = () => {
    setSearchLoading(true);
    fetchOwnerGroups()
      .catch(() => undefined)
      .finally(() => {
        setSearchLoading(false);
      });
  };

  const handleClearFilters = () => {
    setBrandFilter('');
    setBrandFilterInput('');
    setEquipmentTypeFilter(null);
    setEquipmentTypeFilterInput(null);
    setEquipmentSubtypeFilter(null);
    setEquipmentSubtypeFilterInput(null);
    setSidebarSearchQuery('');
    setSearchQuery('');
    setSearchQueryInput('');
    setSortByOwnerId({});
    const newParams = new URLSearchParams(location.search);
    newParams.delete('brand');
    newParams.delete('equipment_type_id');
    newParams.delete('equipment_subtype_id');
    newParams.delete('owner');
    newParams.delete('is_public');
    newParams.delete('ownership');
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const handleFilterBrandChange = (v: string) => {
    setBrandFilterInput(v);
  };
  const handleEquipmentTypeChange = (rawValue: string) => {
    const nextType = rawValue ? parseInt(rawValue, 10) : null;
    setEquipmentTypeFilterInput(nextType);
    setEquipmentSubtypeFilterInput(null);
    setEquipmentTypeFilter(nextType);
    setEquipmentSubtypeFilter(null);
  };
  const handleEquipmentSubtypeChange = (rawValue: string) => {
    const nextSubtype = rawValue ? parseInt(rawValue, 10) : null;
    setEquipmentSubtypeFilterInput(nextSubtype);
    setEquipmentSubtypeFilter(nextSubtype);
  };
  const handleTopSearch = () => {
    setSearchQuery(searchQueryInput);
    setBrandFilter(brandFilterInput);
    setEquipmentTypeFilter(equipmentTypeFilterInput);
    setEquipmentSubtypeFilter(equipmentSubtypeFilterInput);
  };
  const refreshPendingInvites = useCallback(() => {
    setPendingInvitesLoading(true);
    equipmentSharingService
      .getSentEquipmentInvitations()
      .then((res) => {
        const all: PendingInvitation[] = Array.isArray(res.data?.data) ? res.data.data : [];
        setPendingInvites(all.filter((i) => i.invitation_type === 'manager_to_athlete'));
      })
      .catch(() => setPendingInvites([]))
      .finally(() => setPendingInvitesLoading(false));
  }, [equipmentSharingService]);

  const handleCancelInvite = (invite: PendingInvitation) => {
    equipmentSharingService
      .cancelEquipmentInvitation(invite.id)
      .then(() => {
        enqueueSnackbar('Invitation cancelled', { variant: 'success', autoHideDuration: 3000 });
        refreshPendingInvites();
      })
      .catch(() => {
        enqueueSnackbar('Failed to cancel invitation', { variant: 'error', autoHideDuration: 3000 });
      });
  };

  const handleResendInvite = (invite: PendingInvitation) => {
    equipmentSharingService
      .cancelEquipmentInvitation(invite.id)
      .then(() => equipmentSharingService.sendEquipmentInvitation('manager_to_athlete', invite.email))
      .then(() => {
        enqueueSnackbar('Invitation resent', { variant: 'success', autoHideDuration: 3000 });
        refreshPendingInvites();
      })
      .catch(() => {
        enqueueSnackbar('Failed to resend invitation', { variant: 'error', autoHideDuration: 3000 });
        refreshPendingInvites();
      });
  };

  const handleOpenSidebar = (equipmentId?: number, _equipmentTypeId?: number, skiTab = 0) => {
    setSelectedEquipmentId(equipmentId);
    setSkiModalInitialTab(skiTab);
    setSidebarOpen(true);
  };

  const handleCloseSidebar = (refresh?: boolean) => {
    refresh && runSearchRefresh();
    setSidebarOpen(false);
    setSelectedEquipmentId(undefined);
    setSkiModalInitialTab(0);
    setAthleteUserIdForModal(undefined);
    setAthleteNameForModal(undefined);
    const newParams = new URLSearchParams(location.search);
    newParams.delete('equipment_id');
    newParams.delete('equipment_tab');
    const next = newParams.toString();
    navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true });
  };

  const handleRequestSort = (ownerKey: number, column: SortColumn) => {
    setSortByOwnerId((prev) => {
      const cur = prev[ownerKey];
      if (cur?.column === column) {
        return {
          ...prev,
          [ownerKey]: { column, direction: cur.direction === 'asc' ? 'desc' : 'asc' },
        };
      }
      return { ...prev, [ownerKey]: { column, direction: 'asc' } };
    });
  };

  const renderSortLabel = (ownerKey: number, column: SortColumn, label: string) => {
    const sort = sortByOwnerId[ownerKey];
    const active = sort?.column === column;
    return (
      <TableSortLabel
        active={active}
        direction={active && sort ? sort.direction : 'asc'}
        onClick={(e) => {
          e.stopPropagation();
          handleRequestSort(ownerKey, column);
        }}
      >
        {label}
      </TableSortLabel>
    );
  };

  const renderEquipmentTableSection = (
    title: string,
    items: SearchEquipmentItem[],
    ownerKey: number,
    distanceUnit: string,
    variant: EquipmentTableVariant
  ) => {
    if (items.length === 0) return null;

    return (
      <Box key={variant}>
        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.75 }}>
          {title} ({items.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={EQUIPMENT_TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell>{renderSortLabel(ownerKey, 'name', 'Name')}</TableCell>
                <TableCell>{renderSortLabel(ownerKey, 'brand', 'Brand')}</TableCell>
                <TableCell>{renderSortLabel(ownerKey, 'year', 'Year')}</TableCell>
                {variant !== 'other' && <TableCell>{renderSortLabel(ownerKey, 'subtype', 'Subtype')}</TableCell>}
                <TableCell>{renderSortLabel(ownerKey, 'distance_used', 'Distance used')}</TableCell>
                <TableCell>{renderSortLabel(ownerKey, 'default_sport', 'Default sport')}</TableCell>
                {variant === 'skis' && (
                  <>
                    <TableCell>Camber Height</TableCell>
                    <TableCell>MF 0.2</TableCell>
                    <TableCell>Snow Designation</TableCell>
                  </>
                )}
                {variant === 'shoes' && (
                  <>
                    <TableCell>Size</TableCell>
                    <TableCell>Terrain Type</TableCell>
                    <TableCell>Use Type</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const sv = item.standard_field_values ?? {};
                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenSidebar(item.id, item.equipment_type?.id)}
                  >
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.brand}</TableCell>
                    <TableCell>{item.year ?? '—'}</TableCell>
                    {variant !== 'other' && <TableCell>{item.equipment_subtype?.name || ''}</TableCell>}
                    <TableCell>{formatDistanceUsedCell(item, distanceUnit)}</TableCell>
                    <TableCell>{formatDefaultSportCell(item)}</TableCell>
                    {variant === 'skis' && (
                      <>
                        <TableCell>{getStandardValue(sv, 'Camber Height')?.value ?? '—'}</TableCell>
                        <TableCell>{getStandardValue(sv, 'MF 0.2')?.value ?? '—'}</TableCell>
                        <TableCell>{getStandardValue(sv, 'Snow Designation')?.value ?? '—'}</TableCell>
                      </>
                    )}
                    {variant === 'shoes' && (
                      <>
                        <TableCell>{combineShoeSizeDisplay(sv)}</TableCell>
                        <TableCell>{getStandardValue(sv, 'Terrain Type')?.value ?? '—'}</TableCell>
                        <TableCell>{getStandardValue(sv, 'Use Type')?.value ?? '—'}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const modals = (
    <>
      <AddEquipmentModal
        open={sidebarOpen && !selectedEquipmentId}
        onClose={handleCloseSidebar}
        athleteUserId={athleteUserIdForModal}
        athleteName={athleteNameForModal}
      />
      <EquipmentModal
        open={sidebarOpen && !!selectedEquipmentId}
        equipmentId={selectedEquipmentId}
        initialTab={skiModalInitialTab}
        onClose={handleCloseSidebar}
      />
    </>
  );
  // Contained list height matches standalone. Embedded page scrolls so the team
  // header can be hidden; sticky search stays visible above this panel.
  const splitPanelHeight = 'calc(100vh - 160px)';

  return (
    <Box
      className={embedded ? undefined : window.localStorage.getItem('sidedrawer') ? 'page-full' : 'page'}
      id={embedded ? undefined : 'page'}
      sx={
        embedded
          ? {
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              px: 2,
              pb: 2,
            }
          : undefined
      }
    >
      {!embedded && <COEHeaderComponent title="Equipment" />}
      <Box
        sx={{
          flexShrink: 0,
          ...(embedded
            ? {
                position: 'sticky',
                top: 0,
                zIndex: 3,
                bgcolor: 'background.default',
                pt: 1,
                mt: 0,
                pb: 0.5,
              }
            : {}),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            my: 1,
            rowGap: 2,
          }}
        >
          <TextField
            value={searchQueryInput}
            onChange={(e) => setSearchQueryInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleTopSearch();
              }
            }}
            placeholder="Search by owner/equipment name"
            size="small"
            InputProps={{
              startAdornment: <SearchIcon style={{ marginRight: 8 }} />,
              endAdornment: searchQueryInput ? (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label="Clear search"
                    onClick={() => {
                      setSearchQueryInput('');
                      setSearchQuery('');
                      setBrandFilter(brandFilterInput);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ minWidth: 275 }}
            variant="outlined"
          />
          <TextField
            label="Brand"
            value={brandFilterInput}
            onChange={(e) => handleFilterBrandChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleTopSearch();
              }
            }}
            size="small"
            InputProps={{
              endAdornment: brandFilterInput ? (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label="Clear brand"
                    onClick={() => {
                      setBrandFilterInput('');
                      setBrandFilter('');
                      setSearchQuery(searchQueryInput);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ maxWidth: 130 }}
          />
          <TextField
            select
            label="Type"
            value={equipmentTypeFilterInput == null ? '' : String(equipmentTypeFilterInput)}
            onChange={(e) => handleEquipmentTypeChange(e.target.value)}
            size="small"
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="">All types</MenuItem>
            {equipmentTypes.map((type) => (
              <MenuItem key={type.id} value={String(type.id)}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Subtype"
            value={equipmentSubtypeFilterInput == null ? '' : String(equipmentSubtypeFilterInput)}
            onChange={(e) => handleEquipmentSubtypeChange(e.target.value)}
            size="small"
            sx={{ minWidth: 100 }}
            disabled={!selectedTypeForInput}
          >
            <MenuItem value="">All subtypes</MenuItem>
            {subtypeOptionsForInput.map((subtype) => (
              <MenuItem key={subtype.id} value={String(subtype.id)}>
                {subtype.name}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" size="small" startIcon={<SearchIcon />} onClick={handleTopSearch}>
            Search
          </Button>
          <Button variant="outlined" size="small" onClick={handleClearFilters}>
            Clear filters
          </Button>
          {searchError && (
            <Typography color="error" variant="body2">
              {searchError}
            </Typography>
          )}
        </Box>
        <EquipmentResultStatsBar summary={searchSummary} loading={pageLoading || searchLoading} />
      </Box>

      {pageLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: splitPanelHeight,
            width: '100%',
          }}
        >
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              height: splitPanelHeight,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Paper
              square
              elevation={0}
              sx={{
                width: { xs: 200, sm: 240 },
                flexShrink: 0,
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              {/* Search bar */}
              <Box sx={{ px: 1.25, py: 1, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                <TextField
                  value={sidebarSearchQuery}
                  onChange={(e) => setSidebarSearchQuery(e.target.value)}
                  placeholder="Search athletes…"
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', bgcolor: 'action.hover', borderRadius: 1.5 },
                  }}
                />
              </Box>

              <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
                {athletesLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: 'calc(100vh - 220px)',
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <List disablePadding sx={{ py: 0.5 }}>
                    {/* Current user row */}
                    {currentUser &&
                      currentUserId != null &&
                      (() => {
                        const selfSelected = selectedOwnerUserId === currentUserId;
                        const selfGroup = ownerGroups.find((g) => g.owner.id === currentUserId);
                        const selfCount = selfGroup?.total_count ?? 0;
                        return (
                          <ListItemButton
                            selected={selfSelected}
                            onClick={() => setSelectedOwnerUserId(currentUserId)}
                            sx={(theme) => ({
                              alignItems: 'center',
                              gap: 1,
                              py: 1,
                              px: 1.25,
                              borderLeft: 3,
                              borderColor: selfSelected ? 'primary.main' : 'transparent',
                              bgcolor: selfSelected ? alpha(theme.palette.primary.main, 0.08) : undefined,
                            })}
                          >
                            <ListItemAvatar sx={{ minWidth: 40, mt: 0 }}>
                              <UserAvatar
                                src={currentUser.profileImage}
                                firstname={currentUser.firstName}
                                lastname={currentUser.lastName}
                                userId={currentUserId}
                              />
                            </ListItemAvatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ fontWeight: selfSelected ? 600 : 500, lineHeight: 1.25 }}
                              >
                                {`${currentUser.firstName} ${currentUser.lastName}`.trim() || 'You'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {selfCount} item{selfCount === 1 ? '' : 's'}
                              </Typography>
                            </Box>
                            <Chip
                              label={String(selfCount)}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 22,
                                flexShrink: 0,
                                '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                              }}
                            />
                          </ListItemButton>
                        );
                      })()}

                    {/* Managed athletes section */}
                    {filteredSidebarOwners.length > 0 && (
                      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 700, letterSpacing: 1.2 }}
                        >
                          MANAGED ATHLETES
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          • {filteredSidebarOwners.length} Athletes
                        </Typography>
                      </Box>
                    )}
                    {filteredSidebarOwners.map((owner) => {
                      const listKey = `owner-${owner.id}`;
                      const ownerName = `${owner.firstname} ${owner.lastname}`.trim() || 'Unknown';
                      const selected = selectedOwnerUserId === owner.id;
                      const matchingGroup = ownerGroups.find((group) => group.owner.id === owner.id);
                      const count = matchingGroup?.total_count ?? 0;
                      return (
                        <ListItemButton
                          key={listKey}
                          selected={selected}
                          onClick={() => setSelectedOwnerUserId(owner.id)}
                          sx={(theme) => ({
                            alignItems: 'center',
                            gap: 1,
                            py: 1,
                            px: 1.25,
                            borderLeft: 3,
                            borderColor: selected ? 'primary.main' : 'transparent',
                            bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : undefined,
                          })}
                        >
                          <ListItemAvatar sx={{ minWidth: 40, mt: 0 }}>
                            <UserAvatar
                              src={owner.profile_image}
                              firstname={owner.firstname}
                              lastname={owner.lastname}
                              userId={owner.id}
                            />
                          </ListItemAvatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              title={ownerName}
                              sx={{ fontWeight: selected ? 600 : 500, lineHeight: 1.25 }}
                            >
                              {ownerName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {count} item{count === 1 ? '' : 's'}
                            </Typography>
                          </Box>
                          <Chip
                            label={String(count)}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              flexShrink: 0,
                              '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                            }}
                          />
                        </ListItemButton>
                      );
                    })}

                    {/* Pending requests section */}
                    {!isTeamMode && (pendingInvitesLoading || pendingInvites.length > 0) && (
                      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 700, letterSpacing: 1.2, display: 'block' }}
                        >
                          PENDING REQUESTS
                        </Typography>
                      </Box>
                    )}
                    {!isTeamMode && pendingInvitesLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : !isTeamMode ? (
                      pendingInvites.map((invite) => {
                        const displayName =
                          invite.firstname && invite.lastname
                            ? `${invite.firstname} ${invite.lastname.charAt(0)}.`
                            : invite.email;
                        return (
                          <Box
                            key={invite.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              py: 0.75,
                              px: 1.25,
                            }}
                          >
                            <UserAvatar firstname={invite.firstname ?? invite.email} lastname={null} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                noWrap
                                title={invite.email}
                                sx={{ fontWeight: 500, lineHeight: 1.25 }}
                              >
                                {displayName}
                              </Typography>
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                Pending
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              title="Resend invitation"
                              onClick={() => handleResendInvite(invite)}
                              sx={{ flexShrink: 0 }}
                            >
                              <ReplayIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Cancel invitation"
                              onClick={() => handleCancelInvite(invite)}
                              sx={{ flexShrink: 0 }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        );
                      })
                    ) : null}
                  </List>
                )}
              </Box>

              {/* Add managed athlete button */}
              {!isTeamMode && (
                <Box sx={{ px: 1.5, py: 1, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ fontSize: '0.75rem' }}
                    onClick={() => setRequestAthleteDialogOpen(true)}
                  >
                    + Add Managed Athlete
                  </Button>
                </Box>
              )}
            </Paper>
            {searchLoading ? (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}
              >
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {(() => {
                    if (!selectedGroupContext) {
                      if (selectedOwnerInfo != null) {
                        const emptyOwnerName =
                          `${selectedOwnerInfo.firstname ?? ''} ${selectedOwnerInfo.lastname ?? ''}`.trim() ||
                          'Unknown';
                        const viewingSelf = isSelfUserId(selectedOwnerInfo.id, currentUserId);
                        const emptyPanelTitle = viewingSelf ? 'Your Equipment' : emptyOwnerName;
                        return (
                          <>
                            <Box
                              sx={{
                                px: 2,
                                py: 1.25,
                                flexShrink: 0,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 2,
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                  {emptyPanelTitle}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mt: 0.25 }}
                                >
                                  No equipment matches the current filters
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="medium"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  setAthleteUserIdForModal(!viewingSelf ? selectedOwnerInfo.id : undefined);
                                  setAthleteNameForModal(!viewingSelf ? emptyOwnerName : undefined);
                                  handleOpenSidebar();
                                }}
                              >
                                Add
                              </Button>
                            </Box>
                            {/* Owner info strip */}
                            <Box sx={{ px: 1, flexShrink: 0 }}>
                              <Box
                                sx={{
                                  bgcolor: (t) => alpha(t.palette.grey[500], 0.1),
                                  border: (theme) => `1px solid ${theme.palette.divider}`,
                                  borderRadius: '10px',
                                  px: 1.5,
                                  py: 0.75,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                  <UserAvatar
                                    src={selectedOwnerInfo.profile_image}
                                    firstname={selectedOwnerInfo.firstname}
                                    lastname={selectedOwnerInfo.lastname}
                                    userId={selectedOwnerInfo.id}
                                    size={30}
                                    fontSize="0.7rem"
                                  />
                                  <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
                                      {[selectedOwnerInfo.firstname, selectedOwnerInfo.lastname]
                                        .filter(Boolean)
                                        .join(' ') || '—'}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}
                                >
                                  {[
                                    { label: 'Gender', value: '–' },
                                    { label: 'Height', value: '–' },
                                    { label: 'Weight', value: '–' },
                                  ].map((stat, i) => (
                                    <React.Fragment key={stat.label}>
                                      {i > 0 && (
                                        <Box
                                          sx={{
                                            width: '1px',
                                            height: 28,
                                            bgcolor: 'divider',
                                            flexShrink: 0,
                                            mx: 1,
                                          }}
                                        />
                                      )}
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          flexShrink: 0,
                                        }}
                                      >
                                        <Typography
                                          sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
                                        >
                                          {stat.value}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: 9,
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            lineHeight: 1.2,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.3,
                                          }}
                                        >
                                          {stat.label}
                                        </Typography>
                                      </Box>
                                    </React.Fragment>
                                  ))}
                                </Box>
                                {viewingSelf && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setManagersDialogOpen(true)}
                                    sx={{
                                      flexShrink: 0,
                                      fontSize: 11,
                                      py: 0.4,
                                      px: 1.25,
                                      minWidth: 0,
                                      color: 'text.primary',
                                      borderColor: 'divider',
                                      bgcolor: (t) => t.palette.grey[50],
                                      borderRadius: '8px',
                                      '&:hover': { borderColor: 'text.secondary' },
                                      textTransform: 'none',
                                    }}
                                  >
                                    View Managers
                                  </Button>
                                )}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <COEEmptyState
                                title="No equipment found"
                                description="No equipment matches your current filters — or this athlete has none yet"
                                primaryActionTitle="Add Equipment"
                                icon={<PlaylistAddIcon style={{ width: 80, height: 80 }} />}
                                primaryAction={() => {
                                  setAthleteUserIdForModal(!viewingSelf ? selectedOwnerInfo.id : undefined);
                                  setAthleteNameForModal(!viewingSelf ? emptyOwnerName : undefined);
                                  handleOpenSidebar();
                                }}
                              />
                            </Box>
                          </>
                        );
                      }
                      return (
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <COEEmptyState
                            title="No equipments found"
                            description="Please add an equipment to get started"
                            primaryActionTitle="Add Equipment"
                            icon={<PlaylistAddIcon style={{ width: 80, height: 80 }} />}
                            primaryAction={() => handleOpenSidebar()}
                          />
                        </Box>
                      );
                    }
                    const { group, ownerKey, sortedItems } = selectedGroupContext;
                    const ownerName =
                      `${group.owner.firstname ?? ''} ${group.owner.lastname ?? ''}`.trim() || 'Unknown';
                    const viewingSelf = isSelfUserId(group.owner.id, currentUserId);
                    const panelOwnerTitle = viewingSelf ? 'Your Equipment' : ownerName;
                    const skiItems = sortedItems.filter(isSkiItem);
                    const shoeItems = sortedItems.filter(isShoeItem);
                    const otherItems = sortedItems.filter((i) => !isSkiItem(i) && !isShoeItem(i));
                    const nSkis = skiItems.length;
                    const nShoes = shoeItems.length;
                    const nOther = otherItems.length;
                    const pieceWord = group.total_count === 1 ? 'piece' : 'pieces';
                    let summaryLine = `${group.total_count} ${pieceWord} of equipment · XC Skis: ${nSkis}, Shoes: ${nShoes}, Other: ${nOther}`;
                    if (sortedItems.length !== group.total_count) {
                      summaryLine += ` · Showing ${sortedItems.length} of ${group.total_count} (type split from loaded rows)`;
                    }
                    const distanceUnit = getMeasurementUnit();
                    return (
                      <>
                        <Box
                          sx={{
                            px: 2,
                            py: 1.25,
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {panelOwnerTitle}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {summaryLine}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                            <ToggleButtonGroup
                              value={viewMode}
                              exclusive
                              size="small"
                              onChange={(_, v: ViewMode | null) => v && setViewMode(v)}
                              aria-label="Grid or list view"
                            >
                              <ToggleButton value="cards" aria-label="Grid view">
                                <ViewModuleIcon fontSize="small" />
                              </ToggleButton>
                              <ToggleButton value="details" aria-label="List view">
                                <TableRowsIcon fontSize="small" />
                              </ToggleButton>
                            </ToggleButtonGroup>
                            <Button
                              variant="contained"
                              size="medium"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setAthleteUserIdForModal(!viewingSelf ? group.owner.id : undefined);
                                setAthleteNameForModal(
                                  !viewingSelf
                                    ? [group.owner.firstname, group.owner.lastname].filter(Boolean).join(' ')
                                    : undefined
                                );
                                handleOpenSidebar();
                              }}
                            >
                              Add
                            </Button>
                          </Box>
                        </Box>
                        {/* Owner info strip */}
                        <Box
                          sx={{
                            px: 1,
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: (t) => alpha(t.palette.grey[500], 0.1),
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                              borderRadius: '10px',
                              px: 1.5,
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            {/* Avatar + name/email */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                              <UserAvatar
                                src={group.owner.profile_image}
                                firstname={group.owner.firstname}
                                lastname={group.owner.lastname}
                                userId={group.owner.id ?? undefined}
                                size={30}
                                fontSize="0.7rem"
                              />
                              <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
                                  {[group.owner.firstname, group.owner.lastname].filter(Boolean).join(' ') || '—'}
                                </Typography>
                                {group.owner.email && (
                                  <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}>
                                    {group.owner.email}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Stat cells */}
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
                              {[
                                {
                                  label: 'Gender',
                                  value: group.owner.sex
                                    ? String(group.owner.sex).charAt(0).toUpperCase() + String(group.owner.sex).slice(1)
                                    : '–',
                                },
                                { label: 'Height', value: '–' },
                                { label: 'Weight', value: '–' },
                              ].map((stat, i) => (
                                <React.Fragment key={stat.label}>
                                  {i > 0 && (
                                    <Box
                                      sx={{
                                        width: '1px',
                                        height: 28,
                                        bgcolor: 'divider',
                                        flexShrink: 0,
                                        mx: 1,
                                      }}
                                    />
                                  )}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
                                    >
                                      {stat.value}
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: 9,
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        lineHeight: 1.2,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.3,
                                      }}
                                    >
                                      {stat.label}
                                    </Typography>
                                  </Box>
                                </React.Fragment>
                              ))}
                            </Box>

                            {/* View Managers button — only for the current user's own equipment */}
                            {viewingSelf && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setManagersDialogOpen(true)}
                                sx={{
                                  flexShrink: 0,
                                  fontSize: 11,
                                  py: 0.4,
                                  px: 1.25,
                                  minWidth: 0,
                                  color: 'text.primary',
                                  borderColor: 'divider',
                                  bgcolor: (t) => t.palette.grey[50],
                                  borderRadius: '8px',
                                  '&:hover': { borderColor: 'text.secondary' },
                                  textTransform: 'none',
                                }}
                              >
                                View Managers
                              </Button>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 1 }}>
                          {viewMode === 'details' ? (
                            <Stack spacing={2}>
                              {renderEquipmentTableSection('XC Skis', skiItems, ownerKey, distanceUnit, 'skis')}
                              {renderEquipmentTableSection('Shoes', shoeItems, ownerKey, distanceUnit, 'shoes')}
                              {renderEquipmentTableSection('Other', otherItems, ownerKey, distanceUnit, 'other')}
                            </Stack>
                          ) : (
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                gap: 1,
                                alignItems: 'start',
                              }}
                            >
                              {sortedItems.map((item) => (
                                <EquipmentSearchCard key={item.id} item={item} onOpen={handleOpenSidebar} />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              </>
            )}
          </Box>
          {modals}
        </>
      )}
      {!isTeamMode && (
        <RequestAthleteManagementDialog
          isOpen={requestAthleteDialogOpen}
          onClose={() => setRequestAthleteDialogOpen(false)}
          onInvitesSent={refreshPendingInvites}
        />
      )}
      <EquipmentManagersDialog isOpen={managersDialogOpen} onClose={() => setManagersDialogOpen(false)} />
    </Box>
  );
};

export default EquipmentPage;
